import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HolderOutlined } from '@ant-design/icons';
import { Button } from 'antd';

/**
 * @dnd-kit/sortable wrapper used by SprayJobCreate for the zones and
 * chemicals lists (item #16 in Damien's UX feedback).
 *
 * Renders the children inside a positioned container and exposes a drag-
 * handle button anchored top-right, sized to match the neighbouring delete
 * button (antd `<Button type="text">` middle) so the two form a clean,
 * unambiguous pair rather than a tiny floating icon next to a bordered
 * trash can.
 *
 *   ┌──────────────────────────────────────────────[grip][trash]──┐
 *   │ Zone 1                                                       │
 *   │ ...                                                          │
 *   └──────────────────────────────────────────────────────────────┘
 *
 * `touchAction: 'none'` prevents the page from scrolling-versus-dragging on
 * touch devices when the user grabs the handle.
 */
const SortableItem = ({ id, children, style }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const wrapperStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    opacity: isDragging ? 0.7 : 1,
    // Lift the dragging card above its siblings so it stays visually on top
    // while moving — without this it can disappear under the next card.
    zIndex: isDragging ? 2 : 'auto',
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.12)' : 'none',
    ...style,
  };

  return (
    <div ref={setNodeRef} style={wrapperStyle}>
      <Button
        type="text"
        size="middle"
        icon={<HolderOutlined style={{ fontSize: 18 }} />}
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder"
        title="Drag to reorder"
        style={{
          // Anchor to the top-right corner, sitting just to the left of the
          // child card's delete button. SortableItem's outer padding is
          // 16px (passed from SprayJobCreate); the delete button's natural
          // top edge sits at the card's content-area top, so we match it
          // with top: 16. right: 56 = 32 button + 8 gap + 16 right padding.
          position: 'absolute',
          top: 16,
          right: 56,
          width: 32,
          height: 32,
          padding: 0,
          cursor: isDragging ? 'grabbing' : 'grab',
          color: '#666',
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          background: '#fff',
          // Stop touch devices from interpreting the grab as a scroll
          // gesture — without this, dragging on mobile is unreliable.
          touchAction: 'none',
          // Drag handle should sit above the card content for click-through.
          zIndex: 1,
        }}
      />
      {children}
    </div>
  );
};

export default SortableItem;
